/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Button, Pagination, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react';
import { IconCalendar } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Constants, formatAmount } from '../../../utils';
import { PaymentService } from '../../../services';
import ModalGetPlan from '../../payment/get-plan';
import { usePayment } from '../../../hooks/usePayment';
import Render from '../../../components/render';
import RenewPlan from '../../payment/renew-plan';

const MODAL_ACTION = {
  NONE: 0,
  GET_PLAN: 1,
  RENEW_PLAN: 2,
}

const Payments = () => {
  const [selectedItem, setSelectedItem] = useState();
  const [modalAction, setModalAction] = useState(MODAL_ACTION.NONE);
  const { payments, planUser, goToPage, hasPendingPayment, isLoading, pagination, reload, initiatePayment, isLoadingPayment } = useFetchTable();

  const onSelectItem = (item, action) => {
    // Evita que pueda realizar el siguiente pago si ya tiene un pago pendiente por aprobación
    // Esto evita que ocurran errores al acumular días de disfrute de un pago previo que no ha sido aprobado
    if (hasPendingPayment)
      return toast.error('Todavía tiene un pago pendiente por aprobación');

    setSelectedItem(item);
    setModalAction(action);
  }

  const closeModal = (reloading = false) => {
    setSelectedItem(null);
    setModalAction(MODAL_ACTION.NONE);
    if (reloading) reload();
  }

  return (
    <div>
      {(isLoading || isLoadingPayment) && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <Render condition={modalAction === MODAL_ACTION.GET_PLAN}>
        <ModalGetPlan
          initPayment={initiatePayment}
          onSuccess={() => closeModal(true)}
          onClose={() => closeModal()}
        />
      </Render>

      <Render condition={modalAction === MODAL_ACTION.RENEW_PLAN}>
        <RenewPlan
          plan={selectedItem}
          initPayment={initiatePayment}
          onSuccess={() => closeModal(true)}
          onClose={() => closeModal()}
        />
      </Render>

      <div className='bg-secondary rounded-lg flex items-center justify-between m-auto p-4 w-1/3 mb-5'>
        {
          planUser !== null && (
            <>
              <div className='text-white flex gap-1 items-center'>
                <IconCalendar size={35} />
                <div>
                  <p>{planUser?.plan?.name}</p>
                  <div className='bg-gray/65 px-3  rounded-full flex justify-center items-center'>
                    <small>Vence {moment(planUser?.next_payment).format('DD-MM-YYYY')}</small>
                  </div>
                </div>
              </div>

              {planUser?.plan?.price > 0 && (
                <Button
                  className="bg-green text-white w-1/4"
                  onPress={() => onSelectItem(planUser, MODAL_ACTION.RENEW_PLAN)}
                >
                  Pagar
                </Button>
              )}

              {planUser?.plan?.price === 0 && (
                <Button
                  className="bg-green text-white w-1/4"
                  onPress={() => onSelectItem(planUser, MODAL_ACTION.GET_PLAN)}
                >
                  Adquirir Plan
                </Button>
              )}
            </>
          )
        }

        {
          planUser === null && (
            <>
              <div className='text-white flex gap-1 items-center'>
                <IconCalendar size={35} />
                <p>Aun no ha adquirido un plan</p>
              </div>
              <Button
                className='bg-green text-white w-1/4'
                onPress={() => onSelectItem(null, MODAL_ACTION.GET_PLAN)}
              >
                Adquirir
              </Button>
            </>
          )
        }
      </div>

      <Table
        aria-label="Historial de pagos"
        color="primary"
        topContentPlacement="inside"
      >
        <TableHeader>
          <TableColumn style={{ textWrap: 'wrap' }}>Referencia</TableColumn>
          <TableColumn style={{ textWrap: 'wrap' }}>Fecha de pago</TableColumn>
          <TableColumn style={{ textWrap: 'wrap' }}>Vence</TableColumn>
          <TableColumn style={{ textWrap: 'wrap' }}>Plan</TableColumn>
          <TableColumn style={{ textWrap: 'wrap' }}>Monto pagado</TableColumn>
          <TableColumn style={{ textWrap: 'wrap' }}>Método de pago</TableColumn>
          <TableColumn style={{ textWrap: 'wrap' }}>Estatus</TableColumn>
        </TableHeader>
        <TableBody items={payments} emptyContent="No hay resultados">
          {(item) => {
            const plan = item?.plan_users.length > 0 ? item.plan_users[0].plan : null;
            const next_payment = item?.plan_users.length > 0 ? item.plan_users[item.plan_users.length - 1]?.next_payment : null;
            return (
              <TableRow key={item.id}>
                <TableCell>{item?.reference}</TableCell>
                <TableCell>{moment(item.created_at).format('DD-MM-YYYY')}</TableCell>
                <TableCell>{moment(next_payment).format('DD-MM-YYYY')}</TableCell>
                <TableCell>{plan?.name}</TableCell>
                <TableCell>{formatAmount(item?.amount, '$')}</TableCell>
                <TableCell>{item?.payment_method?.name}</TableCell>
                <TableCell>{item?.status_text}</TableCell>

              </TableRow>
            )
          }}
        </TableBody>
      </Table>

      <div className="flex w-full justify-center mt-4">
        <Pagination
          showControls
          variant="bordered"
          page={pagination.page}
          total={pagination.pages}
          onChange={goToPage}
        />
      </div>
    </div>
  );
}


const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const [payments, setPayments] = useState([]);
  const [planUser, setPlanUser] = useState(null);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);
  const user = useSelector(state => state.user);
  const { initiatePayment, isLoading } = usePayment({
    onCompletePayment: (data) => {
      if (!data.success) return false
      window.location.reload();
      return fetchData()
    }
  })

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const response = await PaymentService.payment.getPayments({ ...filters, user_id: user.id });
      setHasPendingPayment(response?.hasPendingPayment);
      setPayments(response?.payments);
      setPlanUser(response?.plan_user);
      setCanFetch(true);

    } catch (error) {
      setPayments([]);
      onError(String(error));
    }
  }

  const getCurrentPagination = () => {
    let pag;
    setPagination(s => {
      pag = s;
      return s;
    });
    return pag;
  }

  const onError = (msg) => toast.error(msg);

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    fetchData();
  }

  const goToPage = (page) => {
    const pagination = getCurrentPagination();
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
    }
  }

  const changePerPage = (perPage) => {
    setCanFetch(true);
    setFilters({ ...filters, perPage });
  }

  useEffect(() => {
    fetchData();
  }, [])

  return {
    changePerPage,
    filters,
    goToPage,
    hasPendingPayment,
    initiatePayment,
    isLoading: !canFetch,
    isLoadingPayment: isLoading,
    pagination,
    payments,
    planUser,
    reload,
  }
}

export default Payments;
